import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Search, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { backendGet } from '@/lib/backendClient';
import { matchRestaurantCategory, normalizeCategoriesPayload, normalizeCategoryName } from '@/lib/categories';
import './RestaurantsCategories.css';

export default function RestaurantsCategories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [restaurantsCount, setRestaurantsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let isMounted = true;

    const normalizeList = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.items)) return payload.items;
      if (Array.isArray(payload?.restaurants)) return payload.restaurants;
      if (Array.isArray(payload?.results)) return payload.results;
      return [];
    };

    const loadCategories = async () => {
      setLoading(true);
      try {
        const [categoriesPayload, restaurantsPayload] = await Promise.all([
          backendGet('/api/categories', { skipAuth: true }),
          backendGet('/api/restaurants/', { skipAuth: true }),
        ]);

        const normalized = normalizeCategoriesPayload(normalizeList(categoriesPayload));
        const restaurants = normalizeList(restaurantsPayload);
        const visibleCategories = normalized.filter((category) => {
          const categoryName = normalizeCategoryName(category?.name);
          return restaurants.some((restaurant) => matchRestaurantCategory(restaurant?.categories || restaurant?.category, categoryName));
        });
        const categoriesWithMetrics = visibleCategories.map((category) => {
          const categoryName = normalizeCategoryName(category?.name);
          const storesInCategory = restaurants.filter((restaurant) =>
            matchRestaurantCategory(restaurant?.categories || restaurant?.category, categoryName)
          ).length;
          return {
            ...category,
            store_count: storesInCategory,
          };
        });

        if (!isMounted) return;
        setRestaurantsCount(restaurants.length);
        if (visibleCategories.length > 0) {
          setCategories(categoriesWithMetrics);
        } else {
          setCategories([]);
        }
      } catch {
        if (!isMounted) return;
        setCategories([]);
        setRestaurantsCount(0);
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    loadCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSelectCategory = (name) => {
    navigate(`/restaurantes/categoria/${encodeURIComponent(name)}`);
  };

  const normalizedSearch = String(searchTerm || '').trim().toLowerCase();
  const filteredCategories = useMemo(() => {
    if (!normalizedSearch) return categories;
    return categories.filter((category) => {
      const categoryName = normalizeCategoryName(category?.name).toLowerCase();
      return categoryName.includes(normalizedSearch);
    });
  }, [categories, normalizedSearch]);

  return (
    <div className="restaurants-categories-page">
      <div className="restaurants-categories-container">
        <button
          type="button"
          className="restaurants-categories-back"
          onClick={() => navigate('/')}
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        <header className="restaurants-categories-hero">
          <div className="restaurants-categories-hero-main">
            <span className="restaurants-categories-kicker">
              <Sparkles size={14} />
              Curadoria CorreFood
            </span>
            <h1>Restaurantes por categoria</h1>
            <p>Escolha um segmento para encontrar lojas com mais agilidade e uma experiencia de navegacao mais objetiva.</p>

            <div className="restaurants-categories-search">
              <Search size={18} />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar categoria..."
                aria-label="Buscar categoria"
              />
            </div>

            <div className="restaurants-categories-meta">
              <span className="restaurants-categories-pill">{categories.length} categorias ativas</span>
              <span className="restaurants-categories-pill">{restaurantsCount} lojas disponiveis</span>
            </div>
          </div>

          <div className="restaurants-categories-hero-side">
            <div className="restaurants-categories-stat-card">
              <strong>{filteredCategories.length}</strong>
              <span>Resultados da busca</span>
            </div>
            <div className="restaurants-categories-stat-card">
              <strong>{categories.reduce((acc, category) => acc + Number(category?.store_count || 0), 0)}</strong>
              <span>Relacionamentos categoria x loja</span>
            </div>
          </div>
        </header>

        <section className="restaurants-categories-results" aria-label="Categorias de restaurantes">
          <div className="restaurants-categories-results-head">
            <h2>Categorias disponiveis</h2>
            {searchTerm ? (
              <button
                type="button"
                className="restaurants-categories-clear"
                onClick={() => setSearchTerm('')}
              >
                Limpar busca
              </button>
            ) : null}
          </div>

          {loading ? (
            <div className="restaurants-categories-loading-grid">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="restaurants-categories-skeleton-card" />
              ))}
            </div>
          ) : null}

          {!loading ? (
            filteredCategories.length > 0 ? (
              <div className="restaurants-categories-grid">
                {filteredCategories.map((category) => {
                  const categoryName = normalizeCategoryName(category.name);
                  return (
                    <button
                      type="button"
                      key={category.id}
                      className="restaurants-categories-card"
                      onClick={() => handleSelectCategory(categoryName)}
                    >
                      <div className="restaurants-categories-image">
                        <img src={category.image_url || category.image} alt={categoryName} />
                        <span className="restaurants-categories-badge">
                          {Number(category?.store_count || 0)} lojas
                        </span>
                      </div>
                      <div className="restaurants-categories-card-body">
                        <span className="restaurants-categories-name">{categoryName}</span>
                        <span className="restaurants-categories-description">
                          Explore os melhores estabelecimentos dessa categoria.
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="restaurants-categories-empty">
                <h3>Nenhuma categoria encontrada</h3>
                <p>Tente outro termo de busca ou volte para ver todas as categorias disponiveis.</p>
              </div>
            )
          ) : null}
        </section>
      </div>
    </div>
  );
}
